// CreditNova Document Integrity Schemas
// Types for per-document confidence analysis and arithmetic validation

import { z } from 'zod';

// ===========================================
// Integrity Flag — a single concern about document quality
// ===========================================

export const integrityFlagSchema = z.object({
  /** What kind of check produced this flag */
  category: z.enum([
    'low_confidence',       // Azure DI cell confidence below threshold
    'arithmetic_mismatch',  // Line items don't sum to stated total
    'missing_critical',     // A key field (revenue, total assets, etc.) wasn't found
    'stale_document',       // Document date is >18 months old
  ]),
  severity: z.enum(['high', 'medium', 'low']),
  /** Human-readable description of the concern */
  message: z.string(),
  /** Which field or cell is affected (e.g. "revenue", "totalAssets") */
  field: z.string().optional(),
  /** Actual value found (if applicable) */
  actual: z.string().optional(),
  /** Expected value (if applicable, e.g. for arithmetic checks) */
  expected: z.string().optional(),
  /** Azure DI confidence score for the cell (0-1) */
  confidence: z.number().min(0).max(1).optional(),
});

export type IntegrityFlag = z.infer<typeof integrityFlagSchema>;

// ===========================================
// Integrity Report — aggregate for one document
// ===========================================

export const integrityReportSchema = z.object({
  /** Overall verdict */
  verdict: z.enum(['clean', 'warning', 'alert']),
  /** Average confidence across all cells */
  avgConfidence: z.number().min(0).max(1),
  /** Lowest confidence found on any cell */
  minConfidence: z.number().min(0).max(1),
  /** Number of cells below the low-confidence threshold */
  lowConfidenceCellCount: z.number(),
  /** Total cells analyzed */
  totalCells: z.number(),
  /** Individual flags */
  flags: z.array(integrityFlagSchema),
  /** Timestamp of analysis */
  analyzedAt: z.string(),
});

export type IntegrityReport = z.infer<typeof integrityReportSchema>;
